import { Controller } from "@hotwired/stimulus"

// Live HS-code lookup for the product form. A "Find code by description" button
// opens a modal where the user types a product description; results are scraped
// server-side from the FBR tariff page (/fbr/hs_search). Picking a result fills
// the existing hs_code input and dispatches a change so the product-form
// controller reloads the UOM options.
export default class extends Controller {
  static targets = [
    "field",     // the existing hs_code <input>
    "modal",     // overlay container
    "input",     // search box inside the modal
    "results",   // results list
    "state",     // status / empty / loading / error message area
    "count"      // "N results" label
  ]

  connect() {
    this._debounce = null
    this._onKeydown = this._handleKeydown.bind(this)
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKeydown)
    clearTimeout(this._debounce)
  }

  // ── Modal open/close ────────────────────────────────────────────────────────

  open(event) {
    if (event) event.preventDefault()
    this.modalTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
    document.addEventListener("keydown", this._onKeydown)

    // Seed the search with whatever's already typed in the HS code field.
    const seed = this.fieldTarget.value.trim()
    this.inputTarget.value = seed
    this.resultsTarget.innerHTML = ""
    if (seed.length >= 2) {
      this.search()
    } else {
      this._showState("idle")
    }
    requestAnimationFrame(() => this.inputTarget.focus())
  }

  close(event) {
    if (event) event.preventDefault()
    this.modalTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
    document.removeEventListener("keydown", this._onKeydown)
  }

  closeOnBackdrop(event) {
    if (event.target === this.modalTarget) this.close()
  }

  // ── Search ──────────────────────────────────────────────────────────────────

  search() {
    clearTimeout(this._debounce)
    const q = this.inputTarget.value.trim()
    if (q.length < 2) {
      this._showState("idle")
      return
    }
    this._showState("loading")
    this._debounce = setTimeout(() => this._fetch(q), 350)
  }

  async _fetch(q) {
    try {
      const res = await fetch(`/fbr/hs_search?q=${encodeURIComponent(q)}`, {
        headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" }
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const rows = await res.json()
      // Ignore stale responses if the query changed while in flight.
      if (this.inputTarget.value.trim() !== q) return
      this._renderResults(rows, q)
    } catch {
      this._showState("error")
    }
  }

  _renderResults(rows, query) {
    if (!rows || rows.length === 0) {
      this._showState("empty")
      return
    }

    this._showState("results")
    if (this.hasCountTarget) {
      this.countTarget.textContent = `${rows.length} match${rows.length === 1 ? "" : "es"}`
    }

    this.resultsTarget.innerHTML = rows.map(r => {
      const code = this._escapeHtml(r.code)
      const desc = this._highlight(r.description || "", query)
      return `
        <button type="button"
                class="group w-full text-left px-4 py-3 flex items-start gap-3
                       hover:bg-primary-subtle focus:bg-primary-subtle focus:outline-none transition-colors"
                data-action="click->hs-code-search#select"
                data-code="${this._attr(r.code)}">
          <span class="shrink-0 inline-flex items-center rounded-md bg-primary/10 text-primary
                       font-mono font-semibold text-sm px-2 py-1 tabular-nums">${code}</span>
          <span class="flex-1 min-w-0 text-sm text-foreground leading-snug">${desc}</span>
          <span class="shrink-0 self-center material-symbols-outlined text-[18px] text-transparent
                       group-hover:text-primary group-focus:text-primary transition-colors">arrow_forward</span>
        </button>`
    }).join("")
    this.resultsTarget.scrollTop = 0
  }

  select(event) {
    const code = event.currentTarget.dataset.code
    this.fieldTarget.value = code
    // Notify product-form so it reloads UOM options for the new code.
    this.fieldTarget.dispatchEvent(new Event("blur", { bubbles: true }))
    this.close()
  }

  // ── State display ─────────────────────────────────────────────────────────

  _showState(state) {
    if (!this.hasStateTarget) return
    const messages = {
      idle: {
        icon: "search",
        title: "Search the FBR tariff",
        body: "Type at least 2 characters of a product description."
      },
      loading: {
        icon: "progress_activity",
        title: "Searching FBR…",
        body: "Looking up matching HS codes.",
        spin: true
      },
      empty: {
        icon: "search_off",
        title: "No matches",
        body: "Try a different or shorter description."
      },
      error: {
        icon: "cloud_off",
        title: "Couldn't reach FBR",
        body: "Try again, or type the code manually."
      }
    }

    const isResults = state === "results"
    this.resultsTarget.classList.toggle("hidden", !isResults)
    this.stateTarget.classList.toggle("hidden", isResults)
    if (this.hasCountTarget) this.countTarget.classList.toggle("hidden", !isResults)
    if (isResults) return

    const m = messages[state] || messages.idle
    this.stateTarget.innerHTML = `
      <div class="flex flex-col items-center justify-center text-center px-6 py-12 gap-3">
        <span class="material-symbols-outlined text-3xl text-foreground-subtle ${m.spin ? "animate-spin" : ""}">${m.icon}</span>
        <p class="text-sm font-semibold text-foreground">${m.title}</p>
        <p class="text-xs text-foreground-muted max-w-xs">${m.body}</p>
      </div>`
  }

  _handleKeydown(event) {
    if (event.key === "Escape") this.close()
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  _highlight(text, query) {
    const safe = this._escapeHtml(text)
    const term = query.trim()
    if (!term) return safe
    const re = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig")
    return safe.replace(re, '<mark class="bg-amber-200/60 text-foreground rounded-sm px-0.5">$1</mark>')
  }

  _attr(str) {
    return String(str).replace(/"/g, "&quot;")
  }

  _escapeHtml(str) {
    return String(str).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  }
}
